<template>
  <div class="user-group-add-users-container">
    <!-- 搜索和筛选区 -->
    <el-card class="user-group-add-users-filters" shadow="hover">
      <el-input
        v-model="searchForm.username"
        :placeholder="$t('userManagement.searchUsernameOrEmail')"
        prefix-icon="el-icon-search"
        clearable
        @input="handleSearch"
      ></el-input>
    </el-card>

    <!-- 可选用户列表 -->
    <el-card class="user-group-add-users-list" shadow="hover">
      <el-table
        :data="users"
        stripe
        height="400"
        style="width: 100%"
        v-loading="loading"
        @selection-change="handleSelectionChange"
      >
        <el-table-column type="selection" width="55" :selectable="checkSelectable"></el-table-column>
        <el-table-column prop="username" :label="$t('userManagement.username')"></el-table-column>
        <el-table-column prop="email" :label="$t('userManagement.email')"></el-table-column>
        <el-table-column prop="mobile" :label="$t('userManagement.phone')"></el-table-column>
        <el-table-column prop="status" :label="$t('userManagement.status')">
          <template slot-scope="scope">
            <el-tag :type="scope.row.status === 1 ? 'success' : 'danger'">
              {{ scope.row.status === 1 ? $t('userManagement.active') : $t('userManagement.inactive') }}
            </el-tag>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="user-group-add-users-pagination">
        <el-pagination
          :current-page="currentPage"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="pageSize"
          layout="total, sizes, prev, pager, next, jumper"
          :total="total"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        ></el-pagination>
      </div>
    </el-card>

    <!-- 操作按钮 -->
    <div class="user-group-add-users-actions">
      <el-button @click="handleCancel">{{ $t('common.cancel') }}</el-button>
      <el-button type="primary" @click="handleAdd" :disabled="selectedUsers.length === 0">
        {{ $t('userManagement.addSelected') }}
      </el-button>
    </div>
  </div>
</template>

<script>
import { getUserList } from '@/apis/user/user.js'

export default {
  name: 'UserGroupAddUsers',
  props: {
    groupId: {
      type: [String, Number],
      required: false,
      default: ''
    },
    excludeUserIds: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      searchForm: {
        username: ''
      },
      currentPage: 1,
      pageSize: 10,
      selectedUsers: [],
      users: [],
      total: 0,
      loading: false
    }
  },
  computed: {
    currentGroup() {
      if (!this.groupId) return null
      return this.$store.getters.userGroupById(this.groupId)
    }
  },
  created() {
    this.fetchUsers()
    if (this.groupId && !this.currentGroup) {
      this.$store.dispatch('fetchUserGroup', this.groupId)
    }
  },
  methods: {
    async fetchUsers() {
      const oldUsers = [...this.users]
      const oldTotal = this.total
      this.loading = true
      try {
        const params = {
          page: this.currentPage,
          limit: this.pageSize,
          username: this.searchForm.username
        }
        const res = await getUserList(params)
        
        // request.js 已经处理了 code !== 0 的情况，直接返回 data
        if (res) {
          const rawUsers = res.list || res.records || (Array.isArray(res) ? res : [])
          this.users = rawUsers.map(u => ({
            ...u,
            userid: u.userId || u.userid || u.id || ''
          }))
          this.total = res.total !== undefined ? res.total : (res.totalCount !== undefined ? res.totalCount : this.users.length)
        }
      } catch (error) {
        // 1. 记录详细日志
        console.group('Fetch Users Error Details')
        console.error('Time:', new Date().toLocaleString())
        console.error('Endpoint: /sys/user/list (via getUserList)')
        console.error('Params:', {
          page: this.currentPage,
          limit: this.pageSize,
          username: this.searchForm.username
        })
        console.error('Raw Error:', error)
        console.groupEnd()

        // 2. 显示友好提示
        this.$message.error(this.$t('userManagement.fetchErrorDetailed'))

        // 3. UI 状态回滚
        this.users = oldUsers
        this.total = oldTotal
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.currentPage = 1
      this.fetchUsers()
    },
    handleSizeChange(size) {
      this.pageSize = size
      this.currentPage = 1
      this.fetchUsers()
    },
    handleCurrentChange(currentPage) {
      this.currentPage = currentPage
      this.fetchUsers()
    },
    handleSelectionChange(val) {
      this.selectedUsers = val
    },
    checkSelectable(row) {
      // If we have excludeUserIds (from creation page), use them
      if (this.excludeUserIds && this.excludeUserIds.length > 0) {
        return !this.excludeUserIds.some(id => String(id) === String(row.userid))
      }
      // Otherwise use the current group's user_ids (from detail page)
      if (!this.currentGroup || !this.currentGroup.user_ids) return true
      return !this.currentGroup.user_ids.some(id => String(id) === String(row.userid))
    },
    handleAdd() {
      if (this.selectedUsers.length === 0) {
        this.$message.warning(this.$t('userManagement.pleaseSelectUsers'))
        return
      }
      
      // Ensure the selected users are in the global store
      this.$store.commit('MERGE_USERS', this.selectedUsers)
      
      // If no groupId, it means we are in the creation page
      if (!this.groupId) {
        this.$emit('success', { 
          success: true, 
          selectedUsers: this.selectedUsers 
        })
        return
      }
      
      const selectedUserIds = this.selectedUsers.map(user => user.userid)
      const currentUserIds = this.currentGroup ? [...this.currentGroup.user_ids] : []
      const allIds = [...currentUserIds, ...selectedUserIds]
      const newUserIds = [...new Set(allIds)].filter(id => id !== undefined && id !== null && id !== '')
      
      console.log('Adding users to group:', {
        groupId: this.groupId,
        newUserIds: newUserIds
      })
      
      this.$store.dispatch('updateUserGroup', {
        id: this.groupId,
        user_ids: newUserIds
      }).then(() => {
        this.$message.success(this.$t('userManagement.addUsersSuccess'))
        this.$emit('success', { success: true })
      }).catch(err => {
        console.error('Add users error:', err)
        this.$message.error(this.$t('userManagement.addUsersError'))
      })
    },
    handleCancel() {
      this.$emit('close')
    }
  }
}
</script>

<style scoped>
.user-group-add-users-container {
  padding: 0;
}

.user-group-add-users-filters {
  margin-bottom: 20px;
}

.user-group-add-users-pagination {
  margin-top: 20px;
  text-align: right;
}

.user-group-add-users-actions {
  margin-top: 20px;
  text-align: right;
}

.user-group-add-users-actions button {
  margin-left: 10px;
}

/* Mobile optimizations */
@media screen and (max-width: 768px) {
  .user-group-add-users-container {
    padding: 10px;
  }
  
  .user-group-add-users-actions {
    display: flex;
    flex-direction: column-reverse;
    gap: 10px;
  }
  
  .user-group-add-users-actions button {
    margin-left: 0;
    width: 100%;
  }
}
</style>